import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ChartLine, Envelope, Lock, GoogleLogo, ArrowRight } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const Login = () => {
  const navigate = useNavigate();
  const { login, googleLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 mb-8" data-testid="login-logo">
            <ChartLine className="w-10 h-10 text-[#D4AF37]" weight="duotone" />
            <span className="font-outfit text-2xl font-semibold text-white">
              THE VAULT<span className="text-[#D4AF37]"> by 1000Banks</span>
            </span>
          </Link>

          <h1 className="font-outfit text-3xl font-light text-white mb-2" data-testid="login-title">
            Welcome Back
          </h1>
          <p className="text-zinc-400 mb-8">
            Sign in to continue to your dashboard
          </p>

          {/* Google Login */}
          <Button
            type="button"
            variant="outline"
            className="w-full border-zinc-700 text-white hover:border-[#D4AF37] hover:text-[#D4AF37] mb-6 py-6"
            onClick={googleLogin}
            data-testid="google-login-btn"
          >
            <GoogleLogo className="w-5 h-5 mr-2" weight="bold" />
            Continue with Google
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-zinc-800" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-[#050505] text-zinc-500">or continue with email</span>
            </div>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 mb-6 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-zinc-400 text-sm">Email</Label>
              <div className="relative mt-1">
                <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-[#121212] border-zinc-800 text-white placeholder:text-zinc-600 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                  placeholder="you@example.com"
                  required
                  data-testid="login-email"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-zinc-400 text-sm">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-[#121212] border-zinc-800 text-white placeholder:text-zinc-600 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                  placeholder="••••••••"
                  required
                  data-testid="login-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#D4AF37] text-black font-medium hover:bg-[#E5C158] py-6"
              data-testid="login-submit"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Sign In
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          <p className="text-center text-zinc-500 text-sm mt-6">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#D4AF37] hover:underline" data-testid="login-register-link">
              Create one
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1200)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/60 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <blockquote className="text-white text-xl font-outfit font-light mb-4">
            "THE VAULT by 1000Banks transformed my trading journey. The signals and courses are invaluable."
          </blockquote>
          <p className="text-[#D4AF37]">— Elite Member since 2024</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
